import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { API } from '../lib/constants';
import { WhatsAppButton } from './WhatsAppButton';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

export const ChatWidget = () => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem('chatSessionId');
    if (!sid) {
      sid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now()) + Math.random();
      localStorage.setItem('chatSessionId', sid);
    }
    setSessionId(sid);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0 && t.chat) {
      setMessages([{ role: 'assistant', content: t.chat.welcome }]);
    }
  }, [open, t.chat, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text, language });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: t.chat.errorFallback }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!t.chat) return null;

  return (
    <>
      {/* Launcher bubble */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          aria-label={t.chat.openLabel}
          className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[95] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#5B7FA8] hover:bg-[#4A6D95] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
        >
          <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full ring-2 ring-white animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed inset-x-4 bottom-4 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[380px] z-[95] flex flex-col rounded-2xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden max-h-[55vh] sm:max-h-[80vh]"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-[#5B7FA8] to-[#4A6D95] text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-[13px] sm:text-sm leading-tight">{t.chat.title}</div>
                <div className="text-[10px] sm:text-[11px] opacity-90 leading-tight">{t.chat.subtitle}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.chat.closeLabel}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full hover:bg-white/15 flex items-center justify-center"
              data-testid="chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4 space-y-2.5 sm:space-y-3 bg-slate-900" data-testid="chat-messages">
            {messages.map((m, i) => {
              const isUser = m.role === 'user';
              const hasFallback = !isUser && typeof m.content === 'string' && m.content.includes('[WA_FALLBACK]');
              const cleaned = hasFallback ? m.content.replace(/\[WA_FALLBACK\]/g, '').trim() : m.content;
              // For a fallback message, grab the user question that preceded it so
              // we can pre-fill the WhatsApp DM with that exact question.
              let prefill = '';
              if (hasFallback) {
                for (let j = i - 1; j >= 0; j--) {
                  if (messages[j].role === 'user' && messages[j].content && messages[j].content.trim()) {
                    prefill = messages[j].content.trim();
                    break;
                  }
                }
              }
              return (
                <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      isUser
                        ? 'bg-[#5B7FA8] text-white rounded-br-md whitespace-pre-wrap'
                        : 'bg-white/10 text-slate-100 rounded-bl-md'
                    }`}
                  >
                    {isUser ? (
                      m.content
                    ) : hasFallback ? (
                      <>
                        {cleaned && (
                          <div className="whitespace-pre-wrap mb-2.5">{cleaned}</div>
                        )}
                        <div className="text-slate-200 font-semibold text-sm mb-2" data-testid="chat-wa-number">
                          {t.chat.waNumber}
                        </div>
                        <WhatsAppButton
                          linkKey="contact"
                          text={prefill ? `Hi! ${prefill}` : undefined}
                          testId="chat-wa-fallback-btn"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#5B7FA8] hover:bg-[#4A6D95] text-white font-bold text-xs transition-colors shadow-md shadow-[#5B7FA8]/30"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          {t.chat.waLabel}
                        </WhatsAppButton>
                      </>
                    ) : (
                      <span className="whitespace-pre-wrap">{m.content}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/10 text-slate-300 rounded-2xl rounded-bl-md px-4 py-3 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 bg-slate-950 px-3 py-2.5">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={t.chat.placeholder}
                data-testid="chat-input"
                className="flex-1 min-w-0 resize-none bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#5B7FA8] max-h-28"
                style={{ color: '#fff' }}
              />
              <button
                type="button"
                onClick={send}
                disabled={loading || !input.trim()}
                data-testid="chat-send"
                aria-label={t.chat.sendLabel}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5B7FA8] hover:bg-[#4A6D95] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-500 text-center mt-1.5">{t.chat.disclaimer}</p>
          </div>
        </div>
      )}
    </>
  );
};

// Closure announcement popup — shows on the homepage, dismissible per session
